import {context} from 'vue3-starter/src/typescript/app-context/AppContext';
import {HttpServiceResponse} from 'vue3-starter/src/typescript/http/http';
import {Tag} from '@/profile-creation-wizard/components/for-creation/Tag';
import {AbstractResource} from 'vue3-starter/src/typescript/resource/AbstractResource';
import {ResourceParams} from 'vue3-starter/src/typescript/resource/resource';
import {AbstractResourceBuilder} from 'vue3-starter/src/typescript/resource/AbstractResourceBuilder';
import {ProfileCreationFunnelMode} from '@/profile-creation-wizard/config/steps/ProfileCreationSteps';
import {ExperienceLevel, ProfileCreationForm, IndustrySkill, FunctionalSkill} from '@/profile-creation-wizard/types';

type ProfileCreationRequest = {
    location: string | null;
    countryCode: string | null;
    city: string | null;
    country: string | null;
    administrativeAreaLevel1: string | null;
    administrativeAreaLevel1Code: string | null;
    administrativeAreaLevel2: string | null;
    administrativeAreaLevel2Code: string | null;
    administrativeAreaLevel3: string | null;
    administrativeAreaLevel3Code: string | null;
    administrativeAreaLevel4: string | null;
    administrativeAreaLevel4Code: string | null;
    lat: number | null;
    lon: number | null;

    workPlacePreference: string | null;
    willingnessToTravelInternationally: boolean;

    headline: string;
    experienceLevel: ExperienceLevel;
    tags: string[];
    price: number;
    priceHidden: boolean;
    phoneNumber: string | null;

    families: string[];
    categories: string[];
    preferredFamily: string | null;
    preferredCategory: string | null;
    categorySuggestion: string | null;
    industryExpertises: string[];
    functionalSkills: string[];
    invitationCode: string | null;
    groupInvitationId: string | null;
    languages: LanguageRequest[];
    funnelMode: ProfileCreationFunnelMode;
};

type LanguageRequest = {
    language: {
        code: string;
        name: string;
    };
    level: string;
};

export class ProfileCreationService {
    async create(form: ProfileCreationForm, mode: ProfileCreationFunnelMode): Promise<HttpServiceResponse<ProfileCreationResource>> {
        const familiesAndCategories = form.familiesCategoriesEdition.toRequest();
        const request: ProfileCreationRequest = {
            location: form.address.formattedAddress,
            countryCode: form.address.countryCode,
            city: form.address.city,
            country: form.address.country,
            administrativeAreaLevel1: form.address.administrativeAreaLevel1,
            administrativeAreaLevel1Code: form.address.administrativeAreaLevel1Code,
            administrativeAreaLevel2: form.address.administrativeAreaLevel2,
            administrativeAreaLevel2Code: form.address.administrativeAreaLevel2Code,
            administrativeAreaLevel3: form.address.administrativeAreaLevel3,
            administrativeAreaLevel3Code: form.address.administrativeAreaLevel3Code,
            administrativeAreaLevel4: form.address.administrativeAreaLevel4Code,
            administrativeAreaLevel4Code: form.address.administrativeAreaLevel4Code,
            lat: form.address.lat,
            lon: form.address.lon,

            workPlacePreference: form.workPlacePreference,
            willingnessToTravelInternationally: form.willingnessToTravelInternationally,

            headline: form.headline!,
            experienceLevel: form.experienceLevel!,
            tags: form.tags.map((tag: Tag) => tag.name),
            price: form.price,
            priceHidden: form.priceHide,
            phoneNumber: form.phone,

            families: [...familiesAndCategories.families],
            categories: familiesAndCategories.categories,
            preferredFamily: familiesAndCategories.families.length > 0 ? [...familiesAndCategories.families][0] : null,
            preferredCategory: familiesAndCategories.categories.length > 0 ? familiesAndCategories.categories[0] : null,
            categorySuggestion: familiesAndCategories.categorySuggestion,
            industryExpertises: form.industryExpertises.map((expertise: IndustrySkill) => expertise.humanId),
            functionalSkills: form.functionalSkills.map((functionalSkill: FunctionalSkill) => functionalSkill.humanId),

            invitationCode: form.invitationCode,
            groupInvitationId: form.groupInvitationId,
            languages: form.languages.map((lang) => {
                return {
                    language: {
                        code: lang.code,
                        name: lang.query!,
                    },
                    level: lang.level!,
                };
            }),
            funnelMode: mode,
        };

        const [data, error] = await context.httpService.post(context.registry.entries.createProfile(), request);

        if (error) {
            return [null, error];
        }

        return [resourceBuilder.build(data), null];
    }
}

class ProfileCreationResource extends AbstractResource {
    constructor(resource: ResourceParams) {
        super(resource);
    }
}

class ProfileCreationResourceBuilder extends AbstractResourceBuilder<ProfileCreationResource> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected _buildResource(data: any): ProfileCreationResource {
        return new ProfileCreationResource({
            links: this.links,
        });
    }
}

const resourceBuilder = new ProfileCreationResourceBuilder();
